/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { FeatureState, ProductsDataActions } from '@msdyn365-commerce/retail-proxy';
import { Cart, CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import {
    createGetFeatureStateInput,
    getCustomer,
    GetCustomerInput,
    getFeatureState,
    getSimpleProducts,
    ProductInput,
    ArrayExtensions,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import { getCommercialName } from '../shared/functions/get-commercial-name';

/**
 * Input class for activeCartWithProducts data action.
 */
export class ActiveCartProductsInput implements IActionInput {
    public getCacheKey = () => 'ActiveCartProducts';

    public getCacheObjectType = () => 'ActiveCartProducts';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext) => {
    return new ActiveCartProductsInput();
};

/**
 * Calls the Retail API and returns a cart object based on the passed GetCartInput.
 * @param input
 * @param ctx
 */
export async function getActiveCartProductsAction(input: ActiveCartProductsInput, ctx: IActionContext): Promise<SimpleProduct[]> {
    // If no cart ID is provided in input, we need to create a cart object
    if (!input) {
        throw new Error('[getActiveCartWithProducts]No valid Input was provided, failing');
    }

    const cartState = await getCartState(ctx);
    const cart = cartState.cart;

    const isQuantityLimitsFeatureIsOn: boolean = await isOrderQuantityLimitsFeatureEnabled(ctx);

    if (isQuantityLimitsFeatureIsOn) {
        return getActiveCartProductsActionWhenQuantityLimitsFeatureIsOn(cart, ctx);
    }
    var products: SimpleProduct[];

    // If there are cart lines, make call to get products
    if (!cartState.hasInvoiceLine && cart && cart.CartLines && cart.CartLines.length > 0) {
        ctx.trace('Getting cart product information...');
        products = await getSimpleProducts(
            <ProductInput[]>cart.CartLines.map(cartLine => {
                if (cartLine.ProductId) {
                    return new ProductInput(
                        cartLine.ProductId,
                        ctx.requestContext.apiSettings,
                        undefined,
                        undefined,
                        ctx.requestContext,
                        cartLine.CatalogId
                    );
                }
                return undefined;
            }).filter(Boolean),
            ctx
        )
            .then((products: SimpleProduct[]) => {
                if (products) {
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(
                            new CustomEvent('datalayerEntityLoaded', {
                                detail: {
                                    typeName: 'ACTIVECARTPRODUCTS',
                                    key: 'ACTIVECARTPRODUCTS',
                                    result: products
                                }
                            })
                        );
                    }
                    return products;
                }
                return [];
            })
            .catch((error: Error) => {
                ctx.trace(error.toString());
                ctx.telemetry.error(error.message);
                ctx.telemetry.debug('[getActiveCartWithProducts]Unable to hydrate cart with product information');
                throw new Error('[getActiveCartWithProducts]Unable to hydrate cart with product information');
            });

        if (products) {
            if (ctx.requestContext.app.config?.useCommercialStyleName) {
                for (var product of products) {
                    var cartLine = cart.CartLines.find(x => x.ProductId === product.RecordId);
                    var attributes = await ProductsDataActions.getAttributeValuesAsync(
                        {
                            callerContext: ctx,
                            queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx)
                        },
                        product.RecordId,
                        Number(ctx.requestContext.channel?.RecordId),
                        cartLine?.CatalogId ?? 0
                    );
                    product.Name = getCommercialName(attributes, product.Name, ctx.requestContext.app.config?.commercialStyleNameAttribute);
                }
            }
            return products;
        }
    }

    ctx.trace('[getActiveCartWithProducts]No Products Found in cart');
    return <SimpleProduct[]>[];
}

export const getActiveCartProductsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-products-in-active-cart',
    action: <IAction<SimpleProduct[]>>getActiveCartProductsAction,
    input: createInput
});
export default getActiveCartProductsActionDataAction;

async function getActiveCartProductsActionWhenQuantityLimitsFeatureIsOn(cart: Cart, ctx: IActionContext): Promise<SimpleProduct[]> {
    const productIdsByWarehouseId: Map<string, CartLine[]> = new Map();
    let resultProducts: SimpleProduct[] = [];
    cart.CartLines?.forEach(cartLine =>
        productIdsByWarehouseId.has(cartLine.WarehouseId!)
            ? productIdsByWarehouseId.get(cartLine.WarehouseId!)?.push(cartLine)
            : productIdsByWarehouseId.set(cartLine.WarehouseId!, [cartLine])
    );
    return Promise.all(
        [...productIdsByWarehouseId].map(([entryWarehouseId, entryProductIds]) => {
            return getSimpleProducts(
                <ProductInput[]>entryProductIds
                    .map(cartLine => {
                        if (cartLine.ProductId) {
                            return new ProductInput(
                                cartLine.ProductId,
                                ctx.requestContext.apiSettings,
                                undefined,
                                entryWarehouseId,
                                ctx.requestContext,
                                cartLine.CatalogId
                            );
                        }
                        return undefined;
                    })
                    .filter(Boolean),
                ctx
            ).then((products: SimpleProduct[]) => {
                if (products) {
                    resultProducts = products.reduce((accum, product) => {
                        if (product) {
                            accum.push(product);
                        }
                        return accum;
                    }, resultProducts);
                }
            });
        })
    ).then(() => resultProducts);
}

async function isOrderQuantityLimitsFeatureEnabled(ctx: IActionContext): Promise<boolean> {
    const defaultOrderQuantityLimitsFeatureConfig = ctx.requestContext.app?.platform?.enableDefaultOrderQuantityLimits;
    if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
        return Promise.resolve(false);
    }

    const featureStates = await getFeatureState(createGetFeatureStateInput(ctx), ctx);
    let isQuantityLimitsFeatureEnabledInHq = false;
    if (ArrayExtensions.hasElements(featureStates)) {
        isQuantityLimitsFeatureEnabledInHq =
            featureStates.find(
                (featureState: FeatureState) => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
            )?.IsEnabled || false;
    }

    if (!isQuantityLimitsFeatureEnabledInHq) {
        return false;
    }

    if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
        return Promise.resolve(true);
    }

    return getCustomer(new GetCustomerInput(ctx.requestContext.apiSettings), ctx)
        .then(customerInfo => {
            return (
                !!customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
            );
        })
        .catch((error: Error) => {
            ctx.telemetry.warning(error.message);
            ctx.telemetry.debug('Unable to get customer info');
            return false;
        });
}
